import {Select} from '@wandb/weave/components/Form/Select';
import React, {useCallback, useMemo} from 'react';
import {type SingleValue} from 'react-select';

import {DD_PRIVACY_ALLOW} from '../../util/datadog/privacy';
import {MetricSearchErrorNotice} from './MetricSearchErrorNotice';
import {type MetricKeySearchResult} from './useMetricKeySearch';
import {useMetricSearchState} from './useMetricSearchState';

const DEFAULT_RESULT_LIMIT = 100;

/** Option shape. `label` is what the menu shows; `value` is the metric key. */
export type MetricSearchOption = {
  value: string;
  label: string;
};

/**
 * Sentinel value for the "results truncated" hint row. The menu doesn't page
 * on scroll, so when the server has more results than the requested page we
 * say so rather than silently showing a prefix of the keys. Rendered as a
 * disabled option so it can't be selected.
 */
const RESULT_LIMIT_HINT_VALUE = '__metric_search_result_limit';

const isResultLimitHintOption = (option: MetricSearchOption): boolean =>
  option.value === RESULT_LIMIT_HINT_VALUE;

export type MetricSearchSelectProps = {
  /** Any-match membership on `MetricInfo.types` (e.g. `['number']`). */
  types?: string[];
  resultLimit?: number;
  /**
   * Client-prepended, non-metric options (e.g. reserved x-axis keys, config
   * keys). The server never returns these, so they're filtered here by the
   * typed query, matched against `label`.
   */
  reservedOptions?: MetricSearchOption[];
  /**
   * Keep reserved options in the menu whatever the user types. Off by default,
   * so they filter alongside everything else the way `MetricSearchDropdown`
   * treats its own reserved keys.
   */
  alwaysShowReservedOptions?: boolean;
  /** Per-result predicate (e.g. dropping system metrics). */
  filterResult?: (metric: MetricKeySearchResult) => boolean;
  /**
   * Display label for a metric key, and for a selection that isn't on the
   * loaded page. Defaults to the key itself.
   */
  labelForValue?: (value: string) => string;
  value: string | undefined;
  onChange: (value: string) => void;
  ariaLabel?: string;
  /**
   * Classes for the wrapper holding the select and its failure notice. The
   * wrapper carries no layout of its own, so pass whatever stacks the two and
   * sizes them for the surrounding form (typically `flex flex-col gap-4`).
   */
  className?: string;
  dataTest?: string;
  /**
   * Stable Datadog RUM action name for this select. Names both the control and
   * its options (`<ddActionName> option`), matching `SettingSelect`. Omit it
   * where the picker it replaces didn't report one.
   */
  ddActionName?: string;
  placeholder?: string;
};

/**
 * Single-select metric picker backed by the metric search API, rendered with
 * react-select.
 *
 * The react-select counterpart to `MetricSearchDropdown`, which renders the
 * same behavior with semantic-ui. Use this one where the picker it replaces is
 * a react-select `SettingSelect` or `Select`, so the surrounding controls keep
 * one widget style.
 *
 * Owns the parts every metric picker repeats: reserved options and their
 * client-side filtering, the per-result predicate, keeping the current
 * selection in the menu when it's off the loaded page, the truncation hint,
 * and the failure notice.
 */
export const MetricSearchSelect = ({
  types,
  resultLimit = DEFAULT_RESULT_LIMIT,
  reservedOptions,
  alwaysShowReservedOptions = false,
  filterResult,
  labelForValue,
  value,
  onChange,
  ariaLabel,
  className,
  dataTest,
  ddActionName,
  placeholder,
}: MetricSearchSelectProps) => {
  const {
    results,
    hasMore,
    isLoading,
    error,
    searchErrorMessage,
    retry,
    searchQuery,
    setSearch,
  } = useMetricSearchState({types, resultLimit});

  const toLabel = useCallback(
    (key: string) => labelForValue?.(key) ?? key,
    [labelForValue]
  );

  const options: MetricSearchOption[] = useMemo(
    function buildOptions() {
      // Metric results arrive filtered by the server. Reserved options are
      // client-side, so the typed query has to be applied to them here or
      // they'd stay in the menu no matter what the user types.
      const query = searchQuery.toLowerCase();

      const opts: MetricSearchOption[] = [];
      const seenValues = new Set<string>();
      const add = (option: MetricSearchOption) => {
        if (!seenValues.has(option.value)) {
          seenValues.add(option.value);
          opts.push(option);
        }
      };

      for (const option of reservedOptions ?? []) {
        if (
          alwaysShowReservedOptions ||
          option.label.toLowerCase().includes(query)
        ) {
          add(option);
        }
      }
      for (const metric of results) {
        if (filterResult?.(metric) ?? true) {
          add({value: metric.key, label: toLabel(metric.key)});
        }
      }

      // Last, so the hint sits at the bottom of the menu.
      if (hasMore) {
        opts.push({
          value: RESULT_LIMIT_HINT_VALUE,
          label: `Limited to ${resultLimit} items. Refine search to see other options.`,
        });
      }
      return opts;
    },
    [
      results,
      hasMore,
      searchQuery,
      reservedOptions,
      alwaysShowReservedOptions,
      filterResult,
      toLabel,
      resultLimit,
    ]
  );

  // Keep the current selection rendered even when it's off the loaded page or
  // filtered out by the typed query.
  const selectedOption = useMemo(() => {
    if (value == null) {
      return null;
    }
    return (
      options.find(option => option.value === value) ?? {
        value,
        label: toLabel(value),
      }
    );
  }, [options, value, toLabel]);

  const onSelect = (selection: SingleValue<MetricSearchOption>) => {
    // The hint row is disabled, but ignore it defensively so it can never be
    // persisted as a metric key.
    if (selection == null || isResultLimitHintOption(selection)) {
      return;
    }
    onChange(selection.value);
  };

  const select = (
    <Select<MetricSearchOption, false>
      aria-label={ariaLabel}
      // Results are already server-filtered by the search term.
      filterOption={null}
      formatOptionLabel={({label}) => (
        // Options render in a portal, so they don't inherit the wrapper's
        // action name or allow level and need their own.
        <div
          data-dd-action-name={
            ddActionName != null ? `${ddActionName} option` : undefined
          }
          data-dd-privacy={ddActionName != null ? DD_PRIVACY_ALLOW : undefined}>
          <span>{label}</span>
        </div>
      )}
      isLoading={isLoading}
      isOptionDisabled={isResultLimitHintOption}
      isSearchable
      onChange={onSelect}
      onInputChange={setSearch}
      options={options}
      placeholder={placeholder}
      value={selectedOption}
    />
  );

  // The wrapper groups the select with its failure notice and, where a caller
  // asks for one, carries the Datadog action name, which RUM reads from an
  // ancestor of the clicked control. Unmasked in replay: workspace config, not
  // credentials or account data. Layout is the caller's, via `className`.
  return (
    <div
      className={className}
      data-dd-action-name={ddActionName}
      data-dd-privacy={ddActionName != null ? DD_PRIVACY_ALLOW : undefined}
      data-test={dataTest}>
      <MetricSearchErrorNotice
        searchErrorMessage={searchErrorMessage}
        error={error}
        onRetry={retry}
      />
      {select}
    </div>
  );
};
